/**
 * The preview panel `chart.preview` (W2-02; DESIGN.md §4.8): the toolbar, the stage (the real Phira render with the
 * Edit-mode overlay, previewLoop.ts) and the line readout. The same panel is the Laptop layout's floating mini
 * preview (the shell hosts this slot there). The toolbar layout follows the panel width (toolbarLayout.ts): the
 * default Chart preset at 1280-1536 px (about 380-460 px) and the 420 px mini preview get the two-row layout, in
 * which every toggle is still a button. While Theater or test play shows the stage full-window, the panel says so
 * and offers to come back.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PanelProps } from '../../app/registry/types';
import { useT } from '../../i18n/useT';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { Kbd } from '../../ui/Kbd';
import { runCommand, useEditorValue, useExt, useProjectOpen, useStageState, useTestPlay } from './hooks';
import { registerServices } from './live';
import { cancelParentPick } from './overlays/selection';
import { m } from './preview.messages';
import { previewStageOf } from './previewLoop';
import { EXT_READOUT, readoutOf } from './prefs';
import { PreviewToolbar } from './PreviewToolbar';
import { Readout } from './Readout';
import { toolbarLayoutFor, type ToolbarLayout } from './toolbarLayout';
import s from './Preview.module.css';

export function PreviewPanel(props: PanelProps) {
  const { services } = props;
  const t = useT();
  const open = useProjectOpen(services);
  const theater = useEditorValue(services, (st) => st.preview.theater);
  const testPlay = useTestPlay(services);
  const readout = useExt(services, EXT_READOUT, readoutOf);
  const stage = useStageState(services);
  const elsewhere = theater || testPlay.active;
  const hostRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<ToolbarLayout>('wide');

  useEffect(() => {
    registerServices(services);
  }, [services]);

  // Mount the stage in this panel while a project is open (Theater / test play attach on top of it).
  useEffect(() => {
    const host = hostRef.current;
    if (!open || host === null) return undefined;
    return previewStageOf(services).attach(host, 'panel');
  }, [services, open]);

  // Measured before the first paint, then followed (panel resizes, the mini preview).
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (el === null) return undefined;
    setLayout(toolbarLayoutFor(el.clientWidth));
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? el.clientWidth;
      setLayout(toolbarLayoutFor(w));
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, []);

  return (
    <div
      ref={panelRef}
      className={s.panel}
      data-region="preview"
      tabIndex={-1}
      aria-label={t(m.panelTitle)}
      data-help-id="preview-panel"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && stage.parentPick !== null) {
          e.preventDefault();
          e.stopPropagation();
          cancelParentPick(services);
        }
      }}
    >
      <PreviewToolbar services={services} layout={layout} />
      <div className={s.stageArea} ref={hostRef} data-help-id="preview-stage">
        {!open && (
          <div className={s.stageElsewhere}>
            <EmptyState icon="MonitorPlay" title={{ key: m.emptyTitle }} body={{ key: m.emptyBody }} />
          </div>
        )}
        {open && elsewhere && (
          <div className={s.stageElsewhere} role="status">
            <span>{testPlay.active ? t(m.elsewhereTestPlay) : t(m.elsewhereTheater)}</span>
            {theater && !testPlay.active && (
              <Button
                size="sm"
                icon="Minimize"
                onClick={() => {
                  runCommand(services, 'preview.theater');
                }}
              >
                {t(m.theaterExit)}
              </Button>
            )}
          </div>
        )}
        {open && stage.parentPick !== null && (
          <div className={s.pickBanner} role="status">
            <span>{t(m.parentPickHint)}</span>
            <Kbd chord="Escape" />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                cancelParentPick(services);
              }}
            >
              {t(m.cancel)}
            </Button>
          </div>
        )}
      </div>
      {open && readout && <Readout services={services} />}
    </div>
  );
}
